import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
// import { useForm, Controller } from 'react-hook-form';

// VirtualDropdown Component
const VirtualDropdown = ({
   options = [],
   value = null,
   onChange = () => {},
   placeholder = 'Select...',
   multiple = false,
   displayKey = 'label',
   valueKey = 'value',
   searchable = true,
   clearable = true,
   noOptionsMessage = 'No options',
   className = '',
   pageSize = 20,
   disabled = false,
   renderOption = null,
   error = null,
}) => {
   const [isOpen, setIsOpen] = useState(false);
   const [query, setQuery] = useState('');
   const [focusedIndex, setFocusedIndex] = useState(-1);
   const wrapperRef = useRef(null);
   const listRef = useRef(null);
   const inputRef = useRef(null);

   const filtered = useMemo(() => {
      if (!query) return options;
      const q = query.toLowerCase();
      return options.filter((o) =>
         String(o[displayKey] || '')
            .toLowerCase()
            .includes(q),
      );
   }, [options, query, displayKey]);

   const rowVirtualizer = useVirtualizer({
      count: filtered.length,
      getScrollElement: () => listRef.current,
      estimateSize: () => 56,
      overscan: 5,
   });

   const selectedItems = useMemo(() => {
      if (multiple) {
         return Array.isArray(value) ? options.filter((o) => value.includes(o[valueKey])) : [];
      }
      return options.find((o) => o[valueKey] === value) ? [options.find((o) => o[valueKey] === value)] : [];
   }, [options, value, valueKey, multiple]);

   useEffect(() => {
      const handler = (e) => {
         if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
            setIsOpen(false);
            setFocusedIndex(-1);
         }
      };
      document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
   }, []);

   const handleKeyDown = useCallback(
      (e) => {
         if (!isOpen) return;
         if (e.key === 'ArrowDown') {
            e.preventDefault();
            setFocusedIndex((i) => Math.min(i + 1, filtered.length - 1));
         } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setFocusedIndex((i) => Math.max(i - 1, 0));
         } else if (e.key === 'Enter') {
            e.preventDefault();
            if (focusedIndex >= 0 && filtered[focusedIndex]) {
               selectItem(filtered[focusedIndex]);
            }
         } else if (e.key === 'Escape') {
            e.preventDefault();
            setIsOpen(false);
         }
      },
      [isOpen, filtered, focusedIndex],
   );

   useEffect(() => {
      if (!isOpen) return;
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
   }, [isOpen, handleKeyDown]);

   useEffect(() => {
      if (isOpen && filtered.length > 0) {
         setTimeout(() => {
            if (!multiple && value) {
               const selectedIndex = filtered.findIndex((opt) => opt[valueKey] === value);
               if (selectedIndex >= 0) {
                  rowVirtualizer.scrollToIndex(selectedIndex, { align: 'center' });
                  setFocusedIndex(selectedIndex);
               }
            } else if (multiple && Array.isArray(value) && value.length > 0) {
               const firstSelectedIndex = filtered.findIndex((opt) => value.includes(opt[valueKey]));
               if (firstSelectedIndex >= 0) {
                  rowVirtualizer.scrollToIndex(firstSelectedIndex, { align: 'center' });
                  setFocusedIndex(firstSelectedIndex);
               }
            }
         }, 50);
      }
   }, [isOpen, value, multiple, valueKey, filtered, rowVirtualizer]);

   const toggleOpen = () => {
      if (disabled) return;
      const willOpen = !isOpen;
      setIsOpen(willOpen);

      if (willOpen) {
         setTimeout(() => inputRef.current?.focus(), 100);
      }
   };

   const clear = (e) => {
      e?.stopPropagation();
      if (multiple) onChange([]);
      else onChange(null);
      setQuery('');
   };

   function selectItem(item) {
      if (multiple) {
         const already = Array.isArray(value) ? value.includes(item[valueKey]) : false;
         if (already) {
            onChange(value.filter((v) => v !== item[valueKey]));
         } else {
            onChange([...(Array.isArray(value) ? value : []), item[valueKey]]);
         }
      } else {
         onChange(item[valueKey]);
         setIsOpen(false);
      }
      setQuery('');
      setFocusedIndex(-1);
   }

   const displayValue = () => {
      if (multiple) return null;
      if (!value) return null;
      const sel = options.find((o) => o[valueKey] === value);
      return sel || null;
   };

   const defaultRenderOption = (opt, isSelected, isFocused) => (
      <div className="flex items-center justify-between gap-3 py-3 px-4">
         <div className="flex-1 min-w-0">
            <div className="font-medium text-gray-900 truncate">{opt[displayKey]}</div>
            {opt.description && <div className="text-sm text-gray-500 truncate mt-0.5">{opt.description}</div>}
         </div>
         {isSelected && (
            <svg className="w-5 h-5 text-blue-600 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
               <path
                  fillRule="evenodd"
                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                  clipRule="evenodd"
               />
            </svg>
         )}
      </div>
   );

   const virtualItems = rowVirtualizer.getVirtualItems();
   const firstVisibleIndex = virtualItems[0]?.index ?? 0;
   const lastVisibleIndex = virtualItems[virtualItems.length - 1]?.index ?? 0;

   return (
      <div ref={wrapperRef} className={`relative ${className}`}>
         <button
            type="button"
            disabled={disabled}
            onClick={toggleOpen}
            className={`w-full flex items-center gap-2 px-3 py-1.5 border rounded-lg bg-slate-800/50 transition-all ${
               disabled
                  ? 'opacity-50 cursor-not-allowed border-slate-700'
                  : error
                  ? 'border-red-500 ring-2 ring-red-400'
                  : isOpen
                  ? 'border-blue-500 ring-2 ring-blue-400'
                  : 'border-slate-700 hover:border-slate-700'
            }`}>
            <div className="flex-1 flex items-center gap-2 min-w-0">
               {multiple && selectedItems.length > 0 ? (
                  <div className="flex flex-wrap gap-1.5">
                     {selectedItems.map((s) => (
                        <div
                           key={s[valueKey]}
                           className="flex items-center gap-1.5 bg-blue-50 text-blue-700 px-2.5 py-1 rounded-md text-sm font-medium border border-blue-200">
                           <span className="truncate">{s[displayKey]}</span>
                           <button
                              type="button"
                              onClick={(e) => {
                                 e.stopPropagation();
                                 selectItem(s);
                              }}
                              className="hover:bg-blue-200 rounded-full w-4 h-4 flex items-center justify-center text-blue-600">
                              ×
                           </button>
                        </div>
                     ))}
                  </div>
               ) : (
                  <div className="min-w-0">
                     {displayValue() ? (
                        <div className="truncate text-white font-medium text-sm">{displayValue()[displayKey]}</div>
                     ) : (
                        <div className="truncate text-gray-500">{placeholder}</div>
                     )}
                  </div>
               )}
            </div>

            <div className="flex items-center gap-1">
               {clearable && (multiple ? selectedItems.length > 0 : Boolean(value)) && (
                  <button
                     onClick={clear}
                     type="button"
                     className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-gray-600 transition-colors">
                     <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                        <path
                           fillRule="evenodd"
                           d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                           clipRule="evenodd"
                        />
                     </svg>
                  </button>
               )}
               <svg
                  className={`w-5 h-5 transition-transform text-gray-400 ${isOpen ? 'rotate-180' : ''}`}
                  viewBox="0 0 20 20"
                  fill="currentColor">
                  <path
                     fillRule="evenodd"
                     d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                     clipRule="evenodd"
                  />
               </svg>
            </div>
         </button>

         {error && (
            <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
               <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                  <path
                     fillRule="evenodd"
                     d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                     clipRule="evenodd"
                  />
               </svg>
               {error}
            </p>
         )}

         {isOpen && (
            <div className="absolute z-50 mt-2 w-full rounded-lg shadow-2xl border border-gray-200 bg-white overflow-hidden">
               {searchable && (
                  <div className="p-3 border-b border-gray-200 bg-gray-50">
                     <div className="relative">
                        <svg
                           className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                           fill="none"
                           stroke="currentColor"
                           viewBox="0 0 24 24">
                           <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                           />
                        </svg>
                        <input
                           ref={inputRef}
                           value={query}
                           onChange={(e) => setQuery(e.target.value)}
                           className="w-full pl-9 pr-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                           placeholder="Search..."
                        />
                     </div>
                  </div>
               )}

               <div ref={listRef} className="max-h-80 overflow-auto">
                  {filtered.length === 0 ? (
                     <div className="p-12 text-center">
                        <svg
                           className="mx-auto w-12 h-12 text-gray-300 mb-3"
                           fill="none"
                           stroke="currentColor"
                           viewBox="0 0 24 24">
                           <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                           />
                        </svg>
                        <p className="text-sm text-gray-500">{noOptionsMessage}</p>
                     </div>
                  ) : (
                     <div style={{ height: `${rowVirtualizer.getTotalSize()}px`, position: 'relative' }}>
                        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                           const opt = filtered[virtualRow.index];
                           const isSelected = multiple
                              ? Array.isArray(value) && value.includes(opt[valueKey])
                              : value === opt[valueKey];
                           const isFocused = virtualRow.index === focusedIndex;

                           return (
                              <div
                                 key={virtualRow.key}
                                 style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: `${virtualRow.size}px`,
                                    transform: `translateY(${virtualRow.start}px)`,
                                 }}>
                                 <button
                                    type="button"
                                    onMouseEnter={() => setFocusedIndex(virtualRow.index)}
                                    onClick={() => selectItem(opt)}
                                    className={`w-full text-left transition-colors border-b border-gray-100 ${
                                       isSelected ? 'bg-blue-50' : isFocused ? 'bg-gray-100' : 'hover:bg-gray-50'
                                    }`}>
                                    {renderOption
                                       ? renderOption(opt, isSelected, isFocused)
                                       : defaultRenderOption(opt, isSelected, isFocused)}
                                 </button>
                              </div>
                           );
                        })}
                     </div>
                  )}
               </div>

               {filtered.length > 0 && (
                  <div className="px-4 py-2.5 text-xs bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                     <div className="flex items-center gap-2 text-gray-600">
                        <span className="font-medium">
                           Showing {firstVisibleIndex + 1}-{Math.min(lastVisibleIndex + 1, filtered.length)} of{' '}
                           {filtered.length}
                        </span>
                     </div>
                     <div className="text-gray-400">
                        <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-xs">↑</kbd>
                        <kbd className="px-1.5 py-0.5 bg-white border border-gray-300 rounded text-xs ml-1">↓</kbd>
                     </div>
                  </div>
               )}
            </div>
         )}
      </div>
   );
};

// React Hook Form Demo
// const ReactHookFormDemo = () => {
//    const {
//       control,
//       handleSubmit,
//       formState: { errors },
//       watch,
//       reset,
//    } = useForm({
//       defaultValues: {
//          country: '',
//          skills: [],
//          department: null,
//          technologies: [],
//       },
//    });

//    const [submittedData, setSubmittedData] = useState(null);

//    const countryOptions = [
//       { label: 'United States', value: 'us', description: 'North America' },
//       { label: 'United Kingdom', value: 'uk', description: 'Europe' },
//       { label: 'Canada', value: 'ca', description: 'North America' },
//       { label: 'Australia', value: 'au', description: 'Oceania' },
//       { label: 'Germany', value: 'de', description: 'Europe' },
//       { label: 'France', value: 'fr', description: 'Europe' },
//       { label: 'Japan', value: 'jp', description: 'Asia' },
//       { label: 'India', value: 'in', description: 'Asia' },
//    ];

//    const skillOptions = [
//       { label: 'JavaScript', value: 'js' },
//       { label: 'TypeScript', value: 'ts' },
//       { label: 'React', value: 'react' },
//       { label: 'Vue.js', value: 'vue' },
//       { label: 'Angular', value: 'angular' },
//       { label: 'Node.js', value: 'node' },
//       { label: 'Python', value: 'python' },
//       { label: 'Java', value: 'java' },
//    ];

//    const departmentOptions = Array.from({ length: 50 }, (_, i) => ({
//       label: `Department ${i + 1}`,
//       value: `dept_${i + 1}`,
//       description: `Team size: ${Math.floor(Math.random() * 50) + 5} members`,
//    }));

//    const technologyOptions = Array.from({ length: 100 }, (_, i) => ({
//       label: `Technology ${i + 1}`,
//       value: `tech_${i + 1}`,
//       description: `Category: ${['Frontend', 'Backend', 'DevOps', 'Database'][i % 4]}`,
//    }));

//    const onSubmit = (data) => {
//       setSubmittedData(data);
//       console.log('Form Data:', data);
//    };

//    const watchedValues = watch();

//    return (
//       <div className="min-h-screen bg-gradient-to-br from-indigo-50 to-blue-50 p-8">
//          <div className="max-w-4xl mx-auto space-y-6">
//             {/* Header */}
//             <div className="text-center space-y-2">
//                <h1 className="text-4xl font-bold text-gray-900">React Hook Form Integration</h1>
//                <p className="text-lg text-gray-600">VirtualDropdown with validation and form state management</p>
//             </div>

//             {/* Form */}
//             <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-8 space-y-6">
//                {/* Single Select with Validation */}
//                <div>
//                   <label className="block text-sm font-semibold text-gray-700 mb-2">
//                      Country <span className="text-red-500">*</span>
//                   </label>
//                   <Controller
//                      name="country"
//                      control={control}
//                      rules={{ required: 'Please select a country' }}
//                      render={({ field }) => (
//                         <VirtualDropdown
//                            options={countryOptions}
//                            value={field.value}
//                            onChange={field.onChange}
//                            placeholder="Select your country..."
//                            error={errors.country?.message}
//                         />
//                      )}
//                   />
//                </div>

//                {/* Multi Select with Validation */}
//                <div>
//                   <label className="block text-sm font-semibold text-gray-700 mb-2">
//                      Skills <span className="text-red-500">*</span>
//                   </label>
//                   <Controller
//                      name="skills"
//                      control={control}
//                      rules={{
//                         validate: (value) => {
//                            if (!value || value.length === 0) {
//                               return 'Please select at least one skill';
//                            }
//                            if (value.length > 5) {
//                               return 'Maximum 5 skills allowed';
//                            }
//                            return true;
//                         },
//                      }}
//                      render={({ field }) => (
//                         <VirtualDropdown
//                            options={skillOptions}
//                            value={field.value}
//                            onChange={field.onChange}
//                            placeholder="Select your skills..."
//                            multiple
//                            error={errors.skills?.message}
//                         />
//                      )}
//                   />
//                </div>

//                {/* Large Dataset Single Select */}
//                <div>
//                   <label className="block text-sm font-semibold text-gray-700 mb-2">Department</label>
//                   <Controller
//                      name="department"
//                      control={control}
//                      render={({ field }) => (
//                         <VirtualDropdown
//                            options={departmentOptions}
//                            value={field.value}
//                            onChange={field.onChange}
//                            placeholder="Search departments..."
//                         />
//                      )}
//                   />
//                   <p className="mt-1 text-xs text-gray-500">50 departments available</p>
//                </div>

//                {/* Large Dataset Multi Select */}
//                <div>
//                   <label className="block text-sm font-semibold text-gray-700 mb-2">Technologies</label>
//                   <Controller
//                      name="technologies"
//                      control={control}
//                      render={({ field }) => (
//                         <VirtualDropdown
//                            options={technologyOptions}
//                            value={field.value}
//                            onChange={field.onChange}
//                            placeholder="Search technologies..."
//                            multiple
//                         />
//                      )}
//                   />
//                   <p className="mt-1 text-xs text-gray-500">100 technologies available</p>
//                </div>

//                {/* Form Actions */}
//                <div className="flex gap-3 pt-4">
//                   <button
//                      type="button"
//                      onClick={handleSubmit(onSubmit)}
//                      className="px-6 py-2.5 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors">
//                      Submit Form
//                   </button>
//                   <button
//                      type="button"
//                      onClick={() => {
//                         reset();
//                         setSubmittedData(null);
//                      }}
//                      className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors">
//                      Reset
//                   </button>
//                </div>
//             </div>

//             {/* Live Form State */}
//             <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
//                <h3 className="text-lg font-semibold text-gray-900 mb-4">Live Form State</h3>
//                <pre className="bg-gray-50 rounded-lg p-4 text-sm overflow-auto max-h-48 font-mono">
//                   {JSON.stringify(watchedValues, null, 2)}
//                </pre>
//             </div>

//             {/* Submitted Data */}
//             {submittedData && (
//                <div className="bg-green-50 border border-green-200 rounded-xl p-6">
//                   <div className="flex items-start gap-3">
//                      <svg
//                         className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5"
//                         fill="currentColor"
//                         viewBox="0 0 20 20">
//                         <path
//                            fillRule="evenodd"
//                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
//                            clipRule="evenodd"
//                         />
//                      </svg>
//                      <div className="flex-1">
//                         <h3 className="text-lg font-semibold text-green-900 mb-2">Form Submitted Successfully!</h3>
//                         <pre className="bg-white rounded-lg p-4 text-sm overflow-auto max-h-48 border border-green-200 font-mono">
//                            {JSON.stringify(submittedData, null, 2)}
//                         </pre>
//                      </div>
//                   </div>
//                </div>
//             )}

//             {/* Code Example */}
//             <div className="bg-gray-900 text-gray-100 rounded-xl p-6 overflow-auto">
//                <h3 className="text-lg font-semibold mb-4 text-white">Usage Example</h3>
//                <pre className="text-sm font-mono">
//                   {`import { useForm, Controller } from 'react-hook-form';

// const MyForm = () => {
//   const { control, handleSubmit, formState: { errors } } = useForm();

//   return (
//     <div>
//       {/* Single Select */}
//       <Controller
//         name="country"
//         control={control}
//         rules={{ required: 'Please select a country' }}
//         render={({ field }) => (
//           <VirtualDropdown
//             options={countryOptions}
//             value={field.value}
//             onChange={field.onChange}
//             placeholder="Select country..."
//             error={errors.country?.message}
//           />
//         )}
//       />

//       {/* Multi Select */}
//       <Controller
//         name="skills"
//         control={control}
//         rules={{
//           validate: (value) =>
//             value?.length > 0 || 'Select at least one skill'
//         }}
//         render={({ field }) => (
//           <VirtualDropdown
//             options={skillOptions}
//             value={field.value}
//             onChange={field.onChange}
//             multiple
//             error={errors.skills?.message}
//           />
//         )}
//       />
//     </div>
//   );
// };`}
//                </pre>
//             </div>
//          </div>
//       </div>
//    );
// };

export default VirtualDropdown;
